import type { BoQRow, FieldKey, RuleFinding } from '../domain/types';
import { getCell, getCellText } from '../domain/cells';
import { parseNumeric } from '../utils/number';
import { isBlank, normalizeCode, normalizeTextForComparison, normalizeUnit } from '../utils/text';

export const NUMERIC_FIELDS: readonly FieldKey[] = ['quantity', 'unitPrice', 'amount'];
export const CORE_TEXT_FIELDS: readonly FieldKey[] = ['code', 'name', 'description', 'unit'];

export function isFormulaWithoutResult(row: BoQRow, field: FieldKey): boolean {
  const cell = getCell(row, field);
  return cell !== undefined && cell.formula !== null && !cell.formulaResultAvailable;
}

export function isCellBlank(row: BoQRow, field: FieldKey): boolean {
  const cell = getCell(row, field);
  if (cell === undefined) return true;
  if (cell.formula !== null && !cell.formulaResultAvailable) return false;
  return isBlank(cell.value) && isBlank(cell.text);
}

export function cellValueForParsing(row: BoQRow, field: FieldKey): unknown {
  const cell = getCell(row, field);
  if (cell === undefined) return null;
  return isBlank(cell.value) ? cell.text : cell.value;
}

export function finding(
  row: BoQRow,
  field: FieldKey | null,
  overrides: Omit<RuleFinding, 'row' | 'field'> = {},
): RuleFinding {
  return { row, field, ...overrides };
}

export function normalizedCoreSignature(row: BoQRow): string {
  return [
    normalizeTextForComparison(getCellText(row, 'name')),
    normalizeUnit(getCellText(row, 'unit')),
    normalizeTextForComparison(getCellText(row, 'description')),
  ].join('\u001f');
}

export function duplicateSignature(row: BoQRow): string | null {
  const textValues = [
    normalizeCode(getCellText(row, 'code')),
    normalizeTextForComparison(getCellText(row, 'name')),
    normalizeTextForComparison(getCellText(row, 'description')),
    normalizeTextForComparison(getCellText(row, 'unit')),
  ];
  if (textValues.some((value) => value.length === 0)) return null;

  const numericValues: string[] = [];
  for (const field of NUMERIC_FIELDS) {
    if (isFormulaWithoutResult(row, field)) return null;
    const parsed = parseNumeric(cellValueForParsing(row, field));
    if (!parsed.ok) return null;
    numericValues.push(parsed.decimal.toString());
  }
  return [...textValues, ...numericValues].join('\u001f');
}

export function groupRows(
  rows: readonly BoQRow[],
  keyForRow: (row: BoQRow) => string,
): ReadonlyMap<string, readonly BoQRow[]> {
  const groups = new Map<string, BoQRow[]>();
  rows.forEach((row) => {
    const key = keyForRow(row);
    if (key.length === 0) return;
    const group = groups.get(key);
    if (group === undefined) groups.set(key, [row]);
    else group.push(row);
  });
  return groups;
}
